import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";

import React, { useState } from "react";

import { Link } from "react-router-dom";
import { formatApiError, api } from "../lib/api";

import { Check, Crown, Sparkles, Star, Users, X, Loader2 } from "lucide-react";

const TIERS = [
  {
    key: "free",
    name: "Free",
    period: "always",
    features: [
      "Wealth dashboard & net-worth tracking",
      "All 25+ financial calculators",
      "Up to 30 assets & 30 liabilities",
      "Up to 30 insurance policies",
      "Up to 10 goals & 10 documents",
      "Up to 4 nominees in Family Vault",
      "Share with up to 2 members",
      "Banner ads",
    ],
    cta: "Start free",
    to: "/register",
  },

  {
    key: "premium_monthly",
    name: "Premium Monthly",
    period: "per month",
    features: [
      "Everything in Free, unlocked",
      "Unlimited assets, liabilities & insurance",
      "Unlimited goals, documents & nominees",
      "Advanced projections & Monte-Carlo",
      "Scenario comparison",
      "Insurance cover analysis",
      "Net-worth PDF reports & CSV exports",
      "Account Aggregator integration",
      "Family sharing",
      "Ad-free experience",
      "Early access to AI insights",
    ],
    cta: "Get monthly",
  },

  {
    key: "premium_yearly",
    name: "Premium Yearly",
    period: "per year",
    highlight: true,
    badge: "BEST VALUE",
    features: [
      "Everything in Premium Monthly",
      "Save 25% vs monthly billing",
      "Priority support",
      "Early access to new features",
    ],
    cta: "Get yearly",
  },

  {
    key: "family_yearly",
    name: "Family",
    period: "per year (4 members)",
    features: [
      "Everything in Premium, for the family",
      "Up to 4 family members",
      "Shared Family Vault & nominees",
      "Family analytics & sharing",
    ],
    cta: "Get family plan",
  },
];

export const Pricing = () => {
  const [modalOpen, setModalOpen] = useState(false);

  const [selectedPlan, setSelectedPlan] = useState(null);

  const [email, setEmail] = useState("");

  const [loading, setLoading] = useState(false);

  const [success, setSuccess] = useState(false);

  const openModal = (plan) => {
    setSelectedPlan(plan);

    setModalOpen(true);

    setSuccess(false);

    setEmail("");
  };

  const closeModal = () => {
    setModalOpen(false);
  };

  const submitInterest = async () => {
    if (!email?.trim()) {
      alert("Please enter your email");

      return;
    }

    try {
      setLoading(true);

      const response = await api.post("/subscription/register-lead", {
        email: email.trim().toLowerCase(),

        plan_type: selectedPlan.key,
      });

      if (response.data?.success) {
        setSuccess(true);

        return;
      }

      alert("Something went wrong");
    } catch (err) {
      console.error(err);

      alert(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white dark:bg-slate-950">
      <Navbar />

      <section className="relative overflow-hidden">
        <div className="pointer-events-none absolute inset-0 overflow-hidden">
          <div className="absolute left-1/2 top-[-180px] h-[420px] w-[420px] -translate-x-1/2 rounded-full bg-emerald-400/10 blur-3xl" />
        </div>

        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-20">
          <div className="mx-auto max-w-3xl text-center">
            <div className="inline-flex items-center gap-2 rounded-full border border-emerald-200 dark:border-emerald-500/20 bg-emerald-50 dark:bg-emerald-500/10 px-4 py-1.5 text-xs font-bold uppercase tracking-widest text-emerald-700 dark:text-emerald-300">
              <Crown className="h-3.5 w-3.5" />
              Subscription Plans
            </div>

            <h1 className="mt-6 font-display text-4xl sm:text-5xl font-bold tracking-tighter text-slate-900 dark:text-white">
              Unlock your full wealth picture
            </h1>

            <p className="mt-5 text-lg leading-relaxed text-slate-600 dark:text-slate-300">
              Premium unlocks unlimited tracking, advanced projections and
              scenarios, insurance analysis, and exportable net-worth reports —
              all ad-free.
            </p>
          </div>

          <div className="mt-16 grid gap-6 xl:grid-cols-4 md:grid-cols-2">
            {TIERS.map((tier) => (
              <PricingCard
                key={tier.key}
                tierKey={tier.key}
                {...tier}
                onPremiumClick={() => openModal(tier)}
              />
            ))}
          </div>

          <div className="mt-12 text-center">
            <p className="mx-auto max-w-2xl text-xs leading-relaxed text-slate-500">
              Subscriptions auto-renew. Manage anytime in your Apple ID or
              Google Play account. By subscribing you accept our Terms & Privacy
              Policy.
            </p>
          </div>
        </div>
      </section>

      {/* MODAL */}

      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4">
          <div className="w-full max-w-md rounded-3xl bg-white dark:bg-slate-900 p-7 shadow-2xl">
            <div className="flex items-start justify-between">
              <div>
                <div className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-600">
                  Early Access
                </div>

                <h2 className="mt-2 text-2xl font-bold text-slate-900 dark:text-white">
                  {selectedPlan?.name}
                </h2>
              </div>

              <button
                onClick={closeModal}
                className="rounded-xl p-2 hover:bg-slate-100 dark:hover:bg-slate-800"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {!success ? (
              <>
                <p className="mt-4 text-sm leading-relaxed text-slate-600 dark:text-slate-300">
                  Enter your email to reserve early access for this subscription
                  plan.
                </p>

                <input
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="
                    mt-6 h-12 w-full rounded-2xl border
                    border-slate-200 dark:border-slate-700
                    bg-white dark:bg-slate-950
                    px-4 text-sm
                    outline-none
                    focus:border-emerald-500
                  "
                />

                <button
                  onClick={submitInterest}
                  disabled={loading}
                  className="
                    mt-5 flex h-12 w-full items-center justify-center gap-2
                    rounded-2xl bg-emerald-600
                    text-sm font-semibold text-white
                    transition-all hover:bg-emerald-700
                    disabled:opacity-50
                  "
                >
                  {loading ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    "Notify Me"
                  )}
                </button>
              </>
            ) : (
              <div className="py-10 text-center">
                <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-emerald-100 dark:bg-emerald-500/10">
                  <Check className="h-8 w-8 text-emerald-600" />
                </div>

                <h3 className="mt-5 text-xl font-bold text-slate-900 dark:text-white">
                  You're on the list
                </h3>

                <p className="mt-3 text-sm text-slate-600 dark:text-slate-300">
                  We'll notify you when subscriptions go live.
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

function PricingCard({
  tierKey,
  name,
  price,
  period,
  features,
  cta,
  to,
  highlight,
  badge,
  onPremiumClick,
}) {
  const isFree = tierKey === "free";

  return (
    <div
      className={`
        relative flex min-h-[560px] flex-col rounded-3xl border
        bg-white dark:bg-slate-900
        p-7 shadow-sm transition-all duration-300
        hover:-translate-y-1
        ${
          highlight
            ? "border-amber-400 ring-1 ring-amber-400/30 shadow-amber-500/10"
            : "border-slate-200 dark:border-slate-800"
        }
      `}
    >
      {badge && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
          <div className="inline-flex items-center gap-1 rounded-full bg-amber-400 px-3 py-1 text-[10px] font-black uppercase tracking-widest text-slate-900 shadow-sm">
            <Sparkles className="h-3 w-3" />
            {badge}
          </div>
        </div>
      )}

      <div>
        <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-slate-500">
          {name}
        </div>

        <div className="mt-5 flex items-end gap-2">
          <span className="font-display text-5xl font-bold leading-none tracking-tight text-slate-900 dark:text-white">
            {price}
          </span>

          <span className="pb-1 text-sm text-slate-500">{period}</span>
        </div>
      </div>

      <div className="mt-8 flex-1">
        <ul className="space-y-4">
          {features.map((feature) => (
            <li
              key={feature}
              className="flex items-start gap-3 text-sm text-slate-700 dark:text-slate-300"
            >
              <div className="mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-emerald-100 dark:bg-emerald-500/10">
                <Check className="h-3.5 w-3.5 text-emerald-600" />
              </div>

              <span className="leading-relaxed">{feature}</span>
            </li>
          ))}
        </ul>
      </div>

      {name === "Family" && (
        <div className="mb-5 flex justify-end">
          <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-emerald-50 dark:bg-emerald-500/10 text-emerald-600">
            <Users className="h-5 w-5" />
          </div>
        </div>
      )}

      {isFree ? (
        <Link
          to={to}
          className={`
            mt-8 inline-flex h-12 w-full items-center justify-center gap-2
            rounded-2xl text-sm font-semibold transition-all
            border border-slate-200 dark:border-slate-700
            text-slate-900 dark:text-white
            hover:bg-slate-50 dark:hover:bg-slate-800
          `}
        >
          {cta}
        </Link>
      ) : (
        <button
          onClick={onPremiumClick}
          className={`
            mt-8 inline-flex h-12 w-full items-center justify-center gap-2
            rounded-2xl text-sm font-semibold transition-all
            ${
              highlight
                ? "bg-emerald-600 text-white hover:bg-emerald-700"
                : "border border-slate-200 dark:border-slate-700 text-slate-900 dark:text-white hover:bg-slate-50 dark:hover:bg-slate-800"
            }
          `}
        >
          {cta}

          {highlight && <Star className="h-4 w-4" />}
        </button>
      )}
    </div>
  );
}
